import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';

interface CalendarDay {
  date: number;
  fullDate: Date;
  status: 'available' | 'occupied' | 'pending' | 'past';
  isToday: boolean;
}

@Component({
  selector: 'app-day-details-modal',
  standalone: true,
  imports: [CommonModule, DatePipe, TranslatePipe],
  template: `
    @if (show && selectedDay) {
      <div class="fixed inset-0 z-[100] flex items-center justify-center bg-bg-base/80 backdrop-blur-md p-3 sm:p-4 animate-fadeIn overflow-y-auto" (click)="onClose()">
        <div class="surface-card p-4 sm:p-6 max-w-md w-full max-h-[calc(100vh-1.5rem)] overflow-y-auto relative transition-all duration-300 transform scale-100" (click)="$event.stopPropagation()">
          <div class="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-primary via-primary-deep to-bg-base"></div>

          <div class="flex justify-between items-start gap-3 mb-5 pt-1">
            <div class="min-w-0">
              <h4 class="caption font-black uppercase tracking-widest font-mono">
                {{ 'unitDetails.dayDetails.title' | translate }}
              </h4>
              <h3 class="text-lg sm:text-xl md:text-2xl font-medium text-text-primary mt-0.5 break-words">
                {{ selectedDay.fullDate | date: 'EEEE, MMMM d, y' }}
              </h3>
            </div>

            <button type="button" (click)="onClose()" class="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-surface-hover border border-border rounded-xl transition-all cursor-pointer active:scale-95">
              <i class="fa-solid fa-xmark text-xs"></i>
            </button>
          </div>

          <div class="space-y-4">
            <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border font-mono text-xs font-black uppercase tracking-wider shadow-sm"
              [ngClass]="{
                'bg-success/10 border-success/20 text-success': selectedDay.status === 'available',
                'bg-danger/10 border-danger/20 text-danger': selectedDay.status === 'occupied',
                'bg-warning/10 border-warning/20 text-warning': selectedDay.status === 'pending',
                'bg-bg-secondary border-border text-text-secondary': selectedDay.status === 'past',
              }">
              <span class="w-2 h-2 rounded-full relative flex">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  [ngClass]="{
                    'bg-success': selectedDay.status === 'available',
                    'bg-danger': selectedDay.status === 'occupied',
                    'bg-warning': selectedDay.status === 'pending',
                    'bg-text-muted': selectedDay.status === 'past',
                  }"></span>
                <span class="relative inline-flex rounded-full h-2 w-2"
                  [ngClass]="{
                    'bg-success': selectedDay.status === 'available',
                    'bg-danger': selectedDay.status === 'occupied',
                    'bg-warning': selectedDay.status === 'pending',
                    'bg-text-muted': selectedDay.status === 'past',
                  }"></span>
              </span>
              <span>{{ 'unitDetails.dayDetails.status' | translate }}: {{ getStatusText(selectedDay.status) }}</span>
            </div>

            <div class="bg-bg-secondary border border-border-subtle rounded-2xl p-4 sm:p-5">
              @switch (selectedDay.status) {
                @case ('available') {
                  <div class="space-y-4">
                    <p class="body-small leading-relaxed">
                      This date segment currently possesses no overlapping historical bounds or
                      leases. It is instantly secure to hold for your upcoming stay.
                    </p>
                    <button type="button" (click)="onBookDate()" class="btn btn-primary w-full py-3">
                      <i class="fa-solid fa-calendar-plus text-xs"></i>
                      <span>{{ 'unitDetails.dayDetails.bookDate' | translate }}</span>
                    </button>
                  </div>
                }

                @case ('occupied') {
                  <div class="space-y-3 font-mono">
                    <div class="caption font-black uppercase tracking-widest text-danger flex items-center gap-1.5">
                      <i class="fa-solid fa-lock text-[9px]"></i> Locked Boundary
                    </div>
                    <div class="space-y-1.5 text-[11px] font-bold text-text-secondary bg-surface p-3 rounded-xl border border-border-subtle shadow-sm">
                      <div class="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-1">
                        <span class="text-text-muted">Booking Node:</span>
                        <span class="text-text-primary">#BK-2026-904</span>
                      </div>
                      <div class="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-1">
                        <span class="text-text-muted">Lease Scope:</span>
                        <span class="text-text-primary">3 Nights</span>
                      </div>
                      <div class="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-1">
                        <span class="text-text-muted">Status Vector:</span>
                        <span class="text-success uppercase">Verified Guest</span>
                      </div>
                    </div>
                  </div>
                }

                @case ('pending') {
                  <div class="space-y-3 font-mono">
                    <div class="caption font-black uppercase tracking-widest text-warning flex items-center gap-1.5">
                      <i class="fa-solid fa-hourglass-half text-[9px]"></i> Escrow Hold
                    </div>
                    <div class="space-y-1.5 text-[11px] font-bold text-text-secondary bg-surface p-3 rounded-xl border border-border-subtle shadow-sm">
                      <div class="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-1">
                        <span class="text-text-muted">Pipeline Hold:</span>
                        <span class="text-text-primary">Initiated 2h ago</span>
                      </div>
                      <div class="flex flex-col min-[380px]:flex-row min-[380px]:justify-between gap-1">
                        <span class="text-text-muted">Resolution SLA:</span>
                        <span class="text-warning">Within 24 Hours</span>
                      </div>
                    </div>
                  </div>
                }

                @case ('past') {
                  <div class="flex items-center gap-2.5 text-text-muted py-2">
                    <i class="fa-solid fa-clock-rotate-left text-sm"></i>
                    <span class="caption font-bold uppercase tracking-wider">Historical boundary outside tactical booking range</span>
                  </div>
                }
              }
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class DayDetailsModalComponent {
  @Input() show: boolean = false;
  @Input() selectedDay?: CalendarDay | null;
  @Output() close = new EventEmitter<void>();
  @Output() bookDate = new EventEmitter<void>();

  onClose() {
    this.close.emit();
  }

  onBookDate() {
    this.bookDate.emit();
    this.close.emit();
  }

  getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      available: 'Available to Book',
      occupied: 'Fully Occupied',
      pending: 'Under Review',
      past: 'Expired History',
    };
    return statusMap[status] || status;
  }
}



